import * as Sentry from '@sentry/react';

/**
 * Sentry filtering helpers
 *
 * Goal: keep high-signal application issues while dropping expected/noisy client-side errors
 * (aborts, transient network errors, auth churn, extensions).
 */

// Common error patterns to ignore
const IGNORED_ERROR_PATTERNS = [
  // Network errors that are typically not actionable
  /Network Error/i,
  /Failed to fetch/i,
  /Load failed/i,
  /NetworkError/i,

  // Browser extension errors
  /extension\//i,
  /extensions\//i,
  /chrome-extension:/i,
  /moz-extension:/i,

  // Ad blocker related errors
  /blocked by client/i,
  /adblocker/i,

  // Third-party widget errors
  /Script error/i,
  /Non-Error promise rejection captured/i,

  // Expected auth-flow error (handled elsewhere with sampled + once-per-session telemetry)
  /AUTH ERROR\. Failed to acquire token\./i,

  // Aborted/cancelled requests (very common and typically not actionable)
  /AbortError/i,
  /signal is aborted/i,
  /Fetch is aborted/i,
  /The user aborted a request/i,

  // Development/debugging errors (can be uncommented for production)
  // /ResizeObserver loop limit exceeded/i,
  // /ResizeObserver loop completed with undelivered notifications/i,
];

// URLs to ignore (external scripts, CDNs, etc.)
const IGNORED_URL_PATTERNS = [
  // Social media widgets
  /connect\.facebook\.net/i,
  /platform\.twitter\.com/i,
  /apis\.google\.com/i,

  // Analytics and tracking
  /google-analytics\.com/i,
  /googletagmanager\.com/i,
  /hotjar\.com/i,

  // Advertisement services
  /doubleclick\.net/i,
  /googlesyndication\.com/i,

  // Browser extensions
  /chrome-extension:/i,
  /moz-extension:/i,
];

// Error types that should have lower priority
const LOW_PRIORITY_ERRORS = [
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  'AbortError',
];

const getEventMessage = (event: Sentry.Event): string =>
  event.message || event.exception?.values?.[0]?.value || '';

const isAbortEvent = (event: Sentry.Event, errorMessage: string): boolean => {
  const exceptionType = event.exception?.values?.[0]?.type || '';
  return (
    exceptionType === 'AbortError' ||
    /AbortError/i.test(exceptionType) ||
    /AbortError|signal is aborted|Fetch is aborted|The user aborted a request/i.test(
      errorMessage,
    )
  );
};

const getHttpStatusFromEvent = (
  event: Sentry.Event,
  errorMessage: string,
): number | null => {
  const match = errorMessage.match(/status code:\s*(\d{3})/i);
  if (match) {
    return Number(match[1]);
  }
  const tagStatus = (event.tags as Record<string, unknown> | undefined)
    ?.httpStatus;
  if (typeof tagStatus === 'string' && /^\d{3}$/.test(tagStatus)) {
    return Number(tagStatus);
  }

  // Try breadcrumbs (fetch/xhr integration often stores status_code there)
  const crumbs = event.breadcrumbs || [];
  for (let i = crumbs.length - 1; i >= 0; i--) {
    const b = crumbs[i];
    if (b?.category === 'fetch' || b?.category === 'xhr') {
      if (b?.data && typeof b.data === 'object' && b.data !== null) {
        const status = (b.data as { status_code?: unknown }).status_code;
        if (typeof status === 'number') {
          return status;
        }
      }
    }
  }

  return null;
};

const getNormalizedUrlPathFromEvent = (event: Sentry.Event): string | null => {
  const candidateUrls: Array<string | undefined> = [
    event.request?.url,
    (event.request?.headers as Record<string, unknown> | undefined)?.referer as
      | string
      | undefined,
  ];

  const crumbs = event.breadcrumbs || [];
  for (let i = crumbs.length - 1; i >= 0; i--) {
    const b = crumbs[i];
    if (b?.category === 'fetch' || b?.category === 'xhr') {
      if (b.data && typeof b.data === 'object') {
        const url = (b.data as { url?: unknown }).url;
        if (typeof url === 'string') {
          candidateUrls.push(url);
          break;
        }
      }
    }
  }

  for (const url of candidateUrls) {
    if (!url) {
      continue;
    }
    try {
      const u = new URL(url, location.origin);
      return u.pathname;
    } catch {
      // ignore
    }
  }

  return null;
};

/**
 * Custom beforeSend filter function
 * Add this to your Sentry configuration as: beforeSend: customErrorFilter
 */
export const customErrorFilter = (event: Sentry.Event): Sentry.Event | null => {
  const errorMessage = getEventMessage(event);

  // Drop abort/cancel noise even if message doesn't match the patterns above.
  if (isAbortEvent(event, errorMessage)) {
    return null;
  }

  // Filter out ignored error patterns
  if (IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(errorMessage))) {
    return null; // Don't send to Sentry
  }

  // Filter out errors from ignored URLs
  const errorUrl =
    event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename || '';
  if (IGNORED_URL_PATTERNS.some(pattern => pattern.test(errorUrl))) {
    return null;
  }

  // Add custom tags for better categorization
  if (LOW_PRIORITY_ERRORS.some(errorType => errorMessage.includes(errorType))) {
    event.tags = {
      ...event.tags,
      priority: 'low',
      category: 'resource-loading',
    };
  }

  // HTTP 5xx errors (commonly emitted by Sentry httpClientIntegration)
  // Group and tag aggressively so bursts don't explode into many issues.
  const httpStatus = getHttpStatusFromEvent(event, errorMessage);
  const httpRoute = getNormalizedUrlPathFromEvent(event);
  if (httpStatus && httpStatus >= 500) {
    event.tags = {
      ...event.tags,
      category: 'http',
      httpStatus: String(httpStatus),
      ...(httpRoute ? { httpRoute } : {}),
    };

    event.fingerprint = [
      'http-error',
      String(httpStatus),
      httpRoute ?? 'unknown',
    ];
  }

  // Add network error categorization
  if (/network|fetch|connection/i.test(errorMessage)) {
    event.tags = {
      ...event.tags,
      category: 'network',
    };

    // Reduce fingerprinting for network errors while preserving route grouping.
    event.fingerprint = [
      'network-error',
      getNormalizedUrlPathFromEvent(event) ?? 'unknown',
    ];
  }

  // Add memory-related error tagging
  if (/memory|heap|allocation/i.test(errorMessage)) {
    event.tags = {
      ...event.tags,
      category: 'memory',
      priority: 'high',
    };
  }

  return event;
};

/**
 * Custom breadcrumb filter
 * Add this to your Sentry configuration in beforeBreadcrumb option
 */
export const customBreadcrumbFilter = (
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null => {
  // Filter out noisy console logs
  if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
    return null;
  }

  // Filter out excessive navigation breadcrumbs
  if (
    breadcrumb.category === 'navigation' &&
    breadcrumb.message?.includes('hash')
  ) {
    return null;
  }

  // Limit HTTP breadcrumbs to errors and important requests
  if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
    const status = breadcrumb.data?.status_code;
    if (status && status >= 200 && status < 400) {
      // Only keep successful requests for important endpoints
      const url = breadcrumb.data?.url || '';
      const importantEndpoints = ['/api/auth', '/api/user', '/api/critical'];

      if (!importantEndpoints.some(endpoint => url.includes(endpoint))) {
        return null;
      }
    }
  }

  return breadcrumb;
};

/**
 * Rate limiting for specific error types
 * Prevents spam of common errors
 */
const errorCounts = new Map<string, { count: number; lastSeen: number }>();
const ERROR_RATE_LIMIT = 5; // Max errors per minute (in-memory)
const HTTP_ERROR_RATE_LIMIT = 2; // More aggressive: HTTP 5xx bursts
const NETWORK_ERROR_RATE_LIMIT = 2; // More aggressive: transient network bursts
const RATE_LIMIT_WINDOW = 60000; // 1 minute

const LOCAL_STORAGE_RATE_LIMIT_PREFIX = 'sentry.ratelimit.v1:';

const cleanupExpiredRateLimits = () => {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    const now = Date.now();
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(LOCAL_STORAGE_RATE_LIMIT_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as { lastSeen?: unknown };
            const lastSeen =
              typeof parsed.lastSeen === 'number' ? parsed.lastSeen : 0;
            if (now - lastSeen > RATE_LIMIT_WINDOW) {
              keysToRemove.push(key);
            }
          } catch {
            keysToRemove.push(key);
          }
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

const getRateLimitFromLocalStorage = (
  key: string,
): { count: number; lastSeen: number } | null => {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const storageKey = `${LOCAL_STORAGE_RATE_LIMIT_PREFIX}${key}`;
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { count?: unknown; lastSeen?: unknown };
    const count = typeof parsed.count === 'number' ? parsed.count : 0;
    const lastSeen = typeof parsed.lastSeen === 'number' ? parsed.lastSeen : 0;
    if (!Number.isFinite(count) || !Number.isFinite(lastSeen)) {
      return null;
    }
    return { count, lastSeen };
  } catch {
    return null;
  }
};

const setRateLimitToLocalStorage = (
  key: string,
  value: { count: number; lastSeen: number },
) => {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(
      `${LOCAL_STORAGE_RATE_LIMIT_PREFIX}${key}`,
      JSON.stringify(value),
    );
  } catch {
    // ignore
  }
};

export const rateLimitedErrorFilter = (
  event: Sentry.Event,
): Sentry.Event | null => {
  // Cleanup expired entries periodically (roughly 1% of the time)
  if (Math.random() < 0.01) {
    cleanupExpiredRateLimits();
  }

  const errorMessage = getEventMessage(event);
  const httpStatus = getHttpStatusFromEvent(event, errorMessage);
  const httpRoute = getNormalizedUrlPathFromEvent(event);

  const isNetworkEvent =
    (event.tags as Record<string, unknown> | undefined)?.category ===
      'network' ||
    /Network Error|Failed to fetch|Load failed|NetworkError/i.test(
      errorMessage,
    );

  // Rate limit HTTP errors by status + route
  const errorKey = httpStatus
    ? `http:${httpStatus}:${httpRoute ?? 'unknown'}`
    : isNetworkEvent
      ? `net:${httpRoute ?? 'unknown'}`
      : `err:${event.exception?.values?.[0]?.type || errorMessage || 'unknown'}`;
  const now = Date.now();

  // Persist rate limiting for the noisiest categories across tabs.
  const persistentLimit = httpStatus
    ? HTTP_ERROR_RATE_LIMIT
    : isNetworkEvent
      ? NETWORK_ERROR_RATE_LIMIT
      : null;
  if (persistentLimit !== null) {
    const stored = getRateLimitFromLocalStorage(errorKey);
    if (!stored || now - stored.lastSeen > RATE_LIMIT_WINDOW) {
      setRateLimitToLocalStorage(errorKey, { count: 1, lastSeen: now });
      return event;
    }

    const next = { count: stored.count + 1, lastSeen: now };
    setRateLimitToLocalStorage(errorKey, next);

    if (next.count > persistentLimit) {
      return null;
    }

    // Early return for persistent limits - don't double-count in the in-memory Map
    return event;
  }

  const errorData = errorCounts.get(errorKey);

  if (!errorData) {
    errorCounts.set(errorKey, { count: 1, lastSeen: now });
    return event;
  }

  // Reset count if outside the window
  if (now - errorData.lastSeen > RATE_LIMIT_WINDOW) {
    errorData.count = 1;
    errorData.lastSeen = now;
    return event;
  }

  // Increment count
  errorData.count++;
  errorData.lastSeen = now;

  // Block if over rate limit
  if (errorData.count > ERROR_RATE_LIMIT) {
    return null;
  }

  return event;
};

/**
 * Example usage in sentry.setup.ts:
 *
 * Sentry.init({
 *   dsn: REACT_APP_SENTRY_DSN,
 *   beforeSend: (event, hint) => {
 *     // Apply multiple filters
 *     event = customErrorFilter(event, hint);
 *     if (!event) return null;
 *
 *     event = rateLimitedErrorFilter(event);
 *     return event;
 *   },
 *   beforeBreadcrumb: customBreadcrumbFilter,
 *   // ... other config
 * });
 */

/**
 * Utility to add custom error filtering rules at runtime
 */
export class SentryErrorFilterManager {
  private static customFilters: Array<
    (event: Sentry.Event, hint: Sentry.EventHint) => Sentry.Event | null
  > = [];

  static addFilter(
    filter: (
      event: Sentry.Event,
      hint: Sentry.EventHint,
    ) => Sentry.Event | null,
  ): void {
    this.customFilters.push(filter);
  }

  static applyFilters(
    event: Sentry.Event,
    hint: Sentry.EventHint,
  ): Sentry.Event | null {
    let filteredEvent: Sentry.Event | null = event;

    for (const filter of this.customFilters) {
      filteredEvent = filter(filteredEvent, hint);
      if (!filteredEvent) {
        return null;
      }
    }
    return filteredEvent;
  }

  static clearFilters(): void {
    this.customFilters = [];
  }
}

// Example of adding a custom filter at runtime:
// SentryErrorFilterManager.addFilter((event, hint) => {
//   if (event.message?.includes('my-custom-error')) {
//     return null; // Filter out
//   }
//   return event;
// });
